import { useMemo } from 'react'

import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import {
  getBedbankMetasearchPriceAccuracyEventData,
  getBedbankRatePublicTotal,
  getCheapestBedbankRateForMetasearchPriceAccuracy,
  selectMetasearchPriceAccuracyCandidate,
} from 'analytics/metasearchPriceAccuracyTracking'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useFireMetasearchPriceAccuracyEvent from 'hooks/OfferPage/useFireMetasearchPriceAccuracyEvent'
import useMetasearchDiscount from 'hooks/useMetasearchDiscount'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCheapestBedbankHotelOnlyRate, getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'

interface Params {
  checkInDate?: string
  checkOutDate?: string
  displayablePackages: Array<App.BedbankOfferPackageView>
  ghaPrice: string | null
  metasearchRoomRateId?: string
  offer: App.BedbankOffer
  rooms: Array<OfferPageRoom>
}

export default function useBedbankMetasearchPriceAccuracyTracking({
  checkInDate,
  checkOutDate,
  displayablePackages,
  ghaPrice,
  metasearchRoomRateId,
  offer,
  rooms,
}: Params) {
  const priceTotal = useAppSelector(appState => {
    const parsed = Number(appState.router.location.query.priceTotal)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
  })

  // Rendered display total for a rate; must match the offer container's derivation so the discount
  // request key (which includes price) resolves.
  const getRatePrice = useMemo(() => (rate: App.BedbankRate): number => {
    if (!checkInDate) {
      return getBedbankRatePublicTotal(rate)
    }
    return getDisplayTotalPrice({
      hotelPrice: getBedbankRatePublicTotal(rate),
      hotelMemberPrice: rate.totals.memberInclusive ?
        rate.totals.memberInclusive + (rate.totals.propertyFees ?? 0) :
        0,
      hotelValue: rate.value,
      checkIn: checkInDate,
      rooms,
      displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
    }).totals.price
  }, [checkInDate, offer.displayPricingAsPerPerson, rooms])

  const candidateRates = useMemo(
    () => displayablePackages.flatMap(displayablePackage => displayablePackage.rates),
    [displayablePackages],
  )
  const fallbackRate = useMemo(
    () => getCheapestBedbankRateForMetasearchPriceAccuracy(displayablePackages[0]?.rates ?? []),
    [displayablePackages],
  )

  // The rate the offer container (and the UI) applies the discount to.
  const metasearchDiscountRateKey = useMemo(() => (
    checkInDate && checkOutDate ? getBedbankRateKey(rooms, checkInDate, checkOutDate) : undefined
  ), [checkInDate, checkOutDate, rooms])
  const metasearchDiscountRates = useAppSelector(state => (
    metasearchDiscountRateKey ? state.offer.bedbankOfferRates[offer.id]?.[metasearchDiscountRateKey] : undefined
  ))
  const discountEligibleRate = useMemo(() => (
    getCheapestBedbankHotelOnlyRate(metasearchDiscountRates ?? [])
  ), [metasearchDiscountRates])

  const [metasearchDiscountAmount, metasearchDiscountFetching, metasearchDiscountSettled] = useMetasearchDiscount(
    discountEligibleRate ? getRatePrice(discountEligibleRate) : 0,
    {
      propertyId: offer.id,
      checkIn: checkInDate,
      checkOut: checkOutDate,
      rooms,
      disabled: !discountEligibleRate,
    },
  )

  const chosen = useMemo(() => selectMetasearchPriceAccuracyCandidate({
    candidates: candidateRates,
    getPrice: getRatePrice,
    isDiscountEligible: rate => !!discountEligibleRate && rate.id === discountEligibleRate.id,
    discountAmount: metasearchDiscountAmount,
    priceTotal,
    fallbackRate,
  }), [candidateRates, getRatePrice, discountEligibleRate, metasearchDiscountAmount, priceTotal, fallbackRate])

  const eventData = useMemo(() => (
    chosen ?
        getBedbankMetasearchPriceAccuracyEventData({
          offerId: offer.id,
          pageType: 'legacy_bedbank',
          metasearchRoomRateId,
          rate: chosen.rate,
          price: chosen.price,
        }) :
      undefined
  ), [chosen, metasearchRoomRateId, offer.id])

  useFireMetasearchPriceAccuracyEvent(
    eventData,
    ghaPrice === '1' && !metasearchDiscountFetching && metasearchDiscountSettled,
    `legacy_bedbank:${offer.id}`,
  )
}
